/**
 * Cash events — Stage Finance timing source of truth.
 *
 * Each event is a real expected cash movement (or a transparent assumption).
 * The monthly ledger / chart only roll these up, plus derived bank draws
 * and repayments computed in buildLedger.
 *
 * Product locks:
 * - Equity + fees = one sponsor funding pot at M0 (opening). No capital-matched
 *   SPV drips unless the user adds an explicit scheduled capital call.
 * - Sales = milestone events (or price-list unit events when available).
 * - Land deposit/closing and construction tranches stay dated.
 * - Feasibility = one early lump. Soft/commercial = small dated defaults.
 * - Financing interest is the only monthly recurring cost (labelled).
 */

import { hydrateAssumptions, normalizeSalesAllocation } from './defaults.js';
import { allocateExact, n } from './math.js';
import { constructionTrancheMonths } from './paymentEvents.js';
import { normalizeConstructionPaymentTranches } from './paymentTiming.js';
import { buildSalesPhaseTargets } from './salesPhases.js';
import { compareCashEvents } from './cashEventAudit.js';

function clampMonth(month, totalMonths) {
  return Math.min(Math.max(1, Math.round(Number(month) || 1)), Math.max(1, totalMonths));
}

function pushEvent(events, partial) {
  const amount = n(partial.amount);
  if (Math.abs(amount) <= 1e-12) return;
  events.push({
    id: partial.id || `${partial.kind}-${events.length + 1}`,
    month: partial.month,
    kind: partial.kind,
    label: partial.label,
    amount,
    direction: partial.direction || 'out',
    payer: partial.payer || null,
    recipient: partial.recipient || null,
    source: partial.source || 'assumption',
    recurring: Boolean(partial.recurring),
    category: partial.category || null,
    meta: partial.meta || {},
  });
}

/** Optional price-list / unit sale rows from P&L outputs (forward-compatible). */
export function extractPriceListSaleEvents(pnl, programme) {
  const rows = Array.isArray(pnl?.priceListUnits) ? pnl.priceListUnits : [];
  if (!rows.length) return [];
  const T = programme.totalMonths;
  const delivery = programme.deliveryMonth;
  const events = [];
  let sum = 0;
  rows.forEach((row, idx) => {
    const value = Math.max(0, n(row.value ?? row.salePrice ?? row.price ?? row.amount));
    if (value <= 0) return;
    const month = clampMonth(
      row.month ?? row.saleMonth ?? row.closingMonth ?? delivery,
      T,
    );
    sum += value;
    events.push({
      id: `sale-unit-${row.id || row.unitId || idx + 1}`,
      month,
      kind: 'sale',
      label: row.label || row.name || `Unit sale ${idx + 1}`,
      amount: value,
      direction: 'in',
      payer: 'buyer',
      recipient: 'spv',
      source: 'price_list',
      recurring: false,
      category: null,
      meta: { unitId: row.id || row.unitId || null, phase: 'price_list' },
    });
  });
  return { events, sum };
}

/**
 * Resolve sale events without silent delivery padding.
 * - Full price-list cover → price_list
 * - Partial price-list → keep units + explicit labelled residual assumption event
 * - Empty / overshooting price-list → fall back to three transparent assumptions
 */
export function resolveSaleEvents(pnl, programme, assumptions) {
  const revenue = Math.max(0, n(pnl.salesRevenue));
  const tol = Math.max(1, revenue * 0.02);
  const extracted = extractPriceListSaleEvents(pnl, programme);
  const unitEvents = extracted.events || [];
  const sum = extracted.sum || 0;

  if (!unitEvents.length) {
    return {
      saleMode: 'assumption',
      saleEvents: assumptionSaleEvents(pnl, programme, assumptions),
    };
  }

  if (revenue > 0 && sum > revenue + tol) {
    // Overshoot is not silently scaled — fall back to transparent assumptions.
    return {
      saleMode: 'assumption',
      saleEvents: assumptionSaleEvents(pnl, programme, assumptions),
    };
  }

  if (revenue > 0 && sum + tol < revenue) {
    const residual = revenue - sum;
    const residualEvent = {
      id: 'sale-residual-assumption',
      month: clampMonth(programme.deliveryMonth, programme.totalMonths),
      kind: 'sale',
      label: 'Sale (residual assumption — price list incomplete)',
      amount: residual,
      direction: 'in',
      payer: 'buyer',
      recipient: 'spv',
      source: 'assumption',
      recurring: false,
      category: null,
      meta: {
        phase: 'residual',
        priceListCover: sum,
        publishedRevenue: revenue,
        residual: true,
      },
    };
    return {
      saleMode: 'price_list_partial',
      saleEvents: [...unitEvents, residualEvent],
    };
  }

  return { saleMode: 'price_list', saleEvents: unitEvents };
}

function assumptionSaleEvents(pnl, programme, assumptions) {
  const allocation = normalizeSalesAllocation(assumptions.salesAllocation);
  const phaseTiming = assumptions.salesPhaseTiming || {};
  const targets = buildSalesPhaseTargets(programme, allocation, phaseTiming);
  if (!targets || !targets.length) return [];
  const revenue = Math.max(0, n(pnl.salesRevenue));
  const labels = {
    preBank: 'Sale (pre-bank assumption)',
    postBank: 'Sale (post-bank assumption)',
    late: 'Sale (late assumption)',
  };
  return targets
    .filter((t) => t.phaseAmount > 1e-12)
    .map((t) => ({
      id: `sale-${t.phase}`,
      month: clampMonth(t.month, programme.totalMonths),
      kind: 'sale',
      label: labels[t.phase] || `Sale (${t.phase})`,
      amount: revenue * t.phaseAmount,
      direction: 'in',
      payer: 'buyer',
      recipient: 'spv',
      source: 'assumption',
      recurring: false,
      category: null,
      meta: { phase: t.phase, allocationShare: t.phaseAmount },
    }));
}

function softCostEvents(amount, programme) {
  const total = Math.max(0, n(amount));
  if (total <= 0) return [];
  const T = programme.totalMonths;
  const m1 = clampMonth(Math.max(1, programme.permitMonth || programme.authorizationEnd || 1), T);
  const m2 = clampMonth(
    Math.round(((programme.constructionStart || m1) + (programme.constructionEnd || m1)) / 2),
    T,
  );
  const m3 = clampMonth(programme.constructionEnd || programme.deliveryMonth || T, T);
  const months = [m1, m2, m3];
  // Collapse duplicate months so allocateExact still sums to total.
  const weights = months.map(() => 1);
  const parts = allocateExact(total, weights);
  const merged = new Map();
  months.forEach((month, i) => {
    merged.set(month, (merged.get(month) || 0) + parts[i]);
  });
  const labels = ['Soft costs (authorization)', 'Soft costs (mid-construction)', 'Soft costs (construction end)'];
  return [...merged.entries()].map(([month, amt], idx) => ({
    id: `soft-${idx + 1}`,
    month,
    kind: 'soft',
    label: labels[Math.min(idx, labels.length - 1)],
    amount: amt,
    direction: 'out',
    payer: 'spv',
    recipient: 'professionals',
    source: 'assumption',
    recurring: false,
    category: 'professionalSoftCosts',
    meta: {},
  }));
}

function commercialEvents(amount, saleEvents, programme) {
  const total = Math.max(0, n(amount));
  if (total <= 0) return [];
  const sales = (saleEvents || []).filter((e) => e.kind === 'sale' && e.amount > 0);
  if (!sales.length) {
    return [{
      id: 'commercial-delivery',
      month: clampMonth(programme.deliveryMonth, programme.totalMonths),
      kind: 'commercial',
      label: 'Commercial / commission (delivery)',
      amount: total,
      direction: 'out',
      payer: 'spv',
      recipient: 'commercial',
      source: 'assumption',
      recurring: false,
      category: 'commercialCosts',
      meta: {},
    }];
  }
  const weights = sales.map((e) => e.amount);
  const parts = allocateExact(total, weights);
  return sales.map((sale, idx) => ({
    id: `commercial-${sale.meta?.phase || idx + 1}`,
    month: sale.month,
    kind: 'commercial',
    label: 'Commission (aligned with sale)',
    amount: parts[idx],
    direction: 'out',
    payer: 'spv',
    recipient: 'commercial',
    source: 'assumption',
    recurring: false,
    category: 'commercialCosts',
    meta: { alignedSaleId: sale.id },
  }));
}

function financingEvents(pnl, programme) {
  const total = Math.max(0, n(pnl.financingCosts));
  if (total <= 0) return [];
  const T = programme.totalMonths;
  const setup = Math.min(total, Math.max(0, n(pnl.bankSetupCost)));
  const interestTotal = Math.max(0, total - setup);
  const events = [];
  const start = clampMonth(programme.acquisitionMonth || 1, T);
  const end = clampMonth(programme.deliveryMonth || T, T);

  if (setup > 0) {
    events.push({
      id: 'financing-setup',
      month: start,
      kind: 'financing_setup',
      label: 'Bank setup fee',
      amount: setup,
      direction: 'out',
      payer: 'spv',
      recipient: 'bank',
      source: 'pnl',
      recurring: false,
      category: 'financingCosts',
      meta: {},
    });
  }

  if (interestTotal > 0) {
    const months = [];
    for (let m = start; m <= end; m += 1) months.push(m);
    const parts = allocateExact(interestTotal, months.map(() => 1));
    months.forEach((month, idx) => {
      events.push({
        id: `interest-${month}`,
        month,
        kind: 'interest',
        label: 'Interest (monthly)',
        amount: parts[idx],
        direction: 'out',
        payer: 'spv',
        recipient: 'bank',
        source: 'pnl',
        recurring: true,
        category: 'financingCosts',
        meta: {},
      });
    });
  }
  return events;
}

/**
 * Build the ordered cash-event schedule.
 * @returns {{ events: object[], saleMode: 'price_list'|'assumption' }}
 */
export function buildCashEvents(pnl, programme, rawAssumptions = {}) {
  const assumptions = hydrateAssumptions(rawAssumptions);
  const T = programme.totalMonths;
  const events = [];

  const feesFunding = Math.max(0, n(pnl.feesFunding ?? pnl.upfrontFees));
  const equity = Math.max(0, n(pnl.sponsorEquity));
  const openingPot = equity + feesFunding;
  pushEvent(events, {
    id: 'sponsor-funding-opening',
    month: 1,
    kind: 'sponsor_funding',
    label: 'Sponsor funding (equity + fees)',
    amount: openingPot,
    direction: 'in',
    payer: 'sponsor',
    recipient: 'spv',
    source: 'pnl',
    recurring: false,
    category: null,
    meta: { equity, feesFunding },
  });

  for (const row of assumptions.scheduledSpvFunding || []) {
    pushEvent(events, {
      id: `capital-call-${row.month}-${events.length}`,
      month: clampMonth(row.month, T),
      kind: 'capital_call',
      label: 'Scheduled capital call',
      amount: Math.max(0, n(row.amount)),
      direction: 'in',
      payer: 'sponsor',
      recipient: 'spv',
      source: 'assumption',
      recurring: false,
      category: null,
      meta: {},
    });
  }

  const landPrice = Math.max(0, n(pnl.landPrice));
  const depositPct = assumptions.landDepositPct || 0;
  const signingMonth = clampMonth(assumptions.landSigningMonth || 1, T);
  const closingMonth = clampMonth(programme.acquisitionMonth, T);
  const depositAmount = landPrice * depositPct;
  const remainderLand = landPrice - depositAmount;

  if (depositAmount > 1e-12) {
    pushEvent(events, {
      id: 'land-deposit',
      month: signingMonth,
      kind: 'land_deposit',
      label: 'Land deposit',
      amount: depositAmount,
      direction: 'out',
      payer: 'spv',
      recipient: 'land_seller',
      source: 'pnl',
      category: 'landPrice',
      meta: { depositPct },
    });
  }
  if (remainderLand > 1e-12 || (depositAmount <= 1e-12 && landPrice > 0)) {
    pushEvent(events, {
      id: 'land-closing',
      month: closingMonth,
      kind: 'land_closing',
      label: depositAmount > 1e-12 ? 'Land closing (balance)' : 'Land closing',
      amount: remainderLand > 0 ? remainderLand : landPrice,
      direction: 'out',
      payer: 'spv',
      recipient: 'land_seller',
      source: 'pnl',
      category: 'landPrice',
      meta: {},
    });
  }
  if (n(pnl.acquisitionCosts) > 0) {
    pushEvent(events, {
      id: 'acquisition-costs',
      month: closingMonth,
      kind: 'acquisition',
      label: 'Acquisition costs',
      amount: n(pnl.acquisitionCosts),
      direction: 'out',
      payer: 'spv',
      recipient: 'notary',
      source: 'pnl',
      category: 'acquisitionCosts',
      meta: {},
    });
  }

  if (n(pnl.feasibilityCosts) > 0) {
    pushEvent(events, {
      id: 'feasibility',
      month: clampMonth(1, T),
      kind: 'feasibility',
      label: 'Feasibility study',
      amount: n(pnl.feasibilityCosts),
      direction: 'out',
      payer: 'spv',
      recipient: 'consultants',
      source: 'pnl',
      recurring: false,
      category: 'feasibilityCosts',
      meta: {},
    });
  }

  const { saleMode, saleEvents } = resolveSaleEvents(pnl, programme, assumptions);
  for (const sale of saleEvents) pushEvent(events, sale);

  for (const soft of softCostEvents(pnl.professionalSoftCosts, programme)) {
    pushEvent(events, soft);
  }
  for (const commercial of commercialEvents(pnl.commercialCosts, saleEvents, programme)) {
    pushEvent(events, commercial);
  }

  const shares = normalizeConstructionPaymentTranches(assumptions.constructionPaymentTranches);
  const trancheMonths = constructionTrancheMonths(programme, shares);
  const build = Math.max(0, n(pnl.constructionCost));
  const reserve = Math.max(0, n(pnl.constructionReserve));
  trancheMonths.forEach((month, idx) => {
    const share = shares[idx] || 0;
    if (build * share > 1e-12) {
      pushEvent(events, {
        id: `construction-build-${idx + 1}`,
        month: clampMonth(month, T),
        kind: 'construction_tranche',
        label: `Construction tranche ${idx + 1} (build)`,
        amount: build * share,
        direction: 'out',
        payer: 'spv',
        recipient: 'contractor',
        source: 'pnl',
        category: 'constructionCost',
        meta: { tranche: idx + 1, share },
      });
    }
    if (reserve * share > 1e-12) {
      pushEvent(events, {
        id: `construction-reserve-${idx + 1}`,
        month: clampMonth(month, T),
        kind: 'construction_tranche',
        label: `Construction tranche ${idx + 1} (reserve)`,
        amount: reserve * share,
        direction: 'out',
        payer: 'spv',
        recipient: 'contractor',
        source: 'pnl',
        category: 'constructionReserve',
        meta: { tranche: idx + 1, share },
      });
    }
  });

  for (const fin of financingEvents(pnl, programme)) pushEvent(events, fin);

  if (n(pnl.taxes) > 0) {
    pushEvent(events, {
      id: 'tax-delivery',
      month: clampMonth(programme.deliveryMonth, T),
      kind: 'tax',
      label: 'Tax',
      amount: n(pnl.taxes),
      direction: 'out',
      payer: 'spv',
      recipient: 'tax_authority',
      source: 'pnl',
      category: 'taxes',
      meta: {},
    });
  }

  events.sort(compareCashEvents);
  return { events, saleMode };
}

/** Month → amount map for capital_call events (ledger / runway SoT). */
export function capitalCallsByMonth(events = []) {
  const map = Object.create(null);
  for (const ev of events) {
    if (!ev || ev.kind !== 'capital_call') continue;
    const month = Number(ev.month);
    const amount = Math.max(0, n(ev.amount));
    if (!Number.isFinite(month) || amount <= 0) continue;
    map[month] = (map[month] || 0) + amount;
  }
  return map;
}
